import i18n from '@/libs/i18n'
import type { IGuestChatOperation, IGuestChatOutcome } from '@/types/Chat'

/**
 * What a confirmed proposal did, said in the guest's own language.
 *
 * The same repair the reception portal made (`pms-web/src/libs/chatResult.ts`) and for
 * the same reason: the service builds its result strings in Go and has no i18n runtime
 * (CLAUDE.md deviation 24), so what travels is a CODE and a COUNT and the sentence is
 * chosen here. The stakes are higher on this side — the reception reads an English
 * half-sentence and shrugs; a guest reads it and does not know whether their room will
 * be made up.
 *
 * # Which of the two wins, decided once
 *
 * THE CODE, resolved in the locale the guest is reading the portal in. The outcome also
 * carries `text`, and that is no longer English — the service writes it out of
 * `Logic.guestCardVocabulary` in the language the CONVERSATION was held in. The two
 * can still differ, because a guest may switch the portal's language after a card
 * has been decided, and then the sheet must read as one language rather than as two.
 * `text` is the service's own words for its audit trail, its logs and the reception
 * side; this file never prefers it.
 *
 * It stays as the FALLBACK, for the two cases that are both real: an operation decided
 * before a code existed, and a code from an API newer than this portal. Showing the
 * service's sentence is worse than a translation and much better than a blank.
 *
 * Nothing here is per-kind and nothing should become so: a code is resolved as a key, so
 * a new outcome is six locale entries and no code at all.
 */

/**
 * Slovenian counts in four forms, and the portal's i18n does not know it.
 *
 * vue-i18n's own pluralisation is left alone deliberately — the 452 lines already in
 * `sl.ts` were written under its DEFAULT rule, and teaching the instance a Slovenian one
 * would silently re-pick every message that uses the two- or three-choice syntax. So the
 * form is chosen here and named as part of the key. Word for word the reasoning in
 * `pms-web/src/libs/chatResult.ts`, and the same code, so the two portals cannot drift
 * into disagreeing about what "2 dni" is.
 *
 * The rule is the CLDR one: 101 is "one" (101 dan) and 102 is "two", which is why it
 * counts modulo a hundred rather than testing the number itself.
 */
const slovenePluralForm = (count: number): string => {
    const rest = Math.abs(Math.trunc(count)) % 100

    if (rest === 1) return 'one'
    if (rest === 2) return 'two'
    if (rest === 3 || rest === 4) return 'few'

    return 'other'
}

/**
 * Everything else counts as English does.
 *
 * Croatian takes the same four forms as Slovenian and is deliberately NOT given them
 * here: the only counted outcome is a number of days, `hr.ts` says it with the number in
 * front of a form that reads for every count, and a plural rule this file cannot test
 * against a native reader is a rule that quietly picks the wrong word. The day a Croatian
 * text needs the distinction it is one branch, beside this comment.
 */
const pluralForm = (locale: string, count: number): string =>
    locale === 'sl' ? slovenePluralForm(count) : (Math.abs(count) === 1 ? 'one' : 'other')

/**
 * Resolves one key, or nothing.
 *
 * vue-i18n answers a missing key with the key itself, which is what a sheet showing
 * "milo.result.guest_service_booked_two" is made of. Comparing the answer with the key
 * is how that is caught before a guest reads it.
 */
const translated = (key: string, values: Record<string, unknown>): string => {
    const line = i18n.global.t(key, values)

    return line === key ? '' : line
}

/**
 * The result line for one outcome, or the fallback when nothing is known about the code.
 *
 * Three keys are tried in order, which is what lets the counted and the uncounted codes
 * live in one flat namespace: `milo.result.<code>_<form>` for a code that counts
 * something, `milo.result.<code>_other` as its own fallback, and plain
 * `milo.result.<code>` for the three that count nothing at all — a late checkout asked
 * for, a room request raised, a message handed to the desk. Four identical strings for
 * each of those would be four chances for three of them to go stale.
 */
export const guestOutcomeLine = (outcome?: IGuestChatOutcome | null, fallback = ''): string => {
    if (!outcome?.code) return fallback

    const locale = String(i18n.global.locale.value)
    const values = { count: outcome.count, detail: outcome.detail }

    const line = translated(`milo.result.${outcome.code}_${pluralForm(locale, outcome.count)}`, values)
        || translated(`milo.result.${outcome.code}_other`, values)
        || translated(`milo.result.${outcome.code}`, values)

    if (!line) return fallback

    // The detail is a fact with no key of its own — the name of the service booked, the
    // room the request was raised for. Messages that want it name {detail} and already
    // carry it; the rest get it appended rather than dropped.
    return outcome.detail && !line.includes(outcome.detail) ? `${line} — ${outcome.detail}` : line
}

/**
 * The same for a whole operation, which is where the pre-outcome fallback lives.
 *
 * The outcome's own `text` before the operation's `result`: they are the same sentence
 * whenever both are there — the service writes `result` from `outcome.text` — and asking
 * for the outcome first keeps the fallback beside the thing it is a fallback for.
 */
export const guestOperationResult = (operation: IGuestChatOperation): string =>
    guestOutcomeLine(operation.outcome, operation.outcome?.text || operation.result)
